// @flow

import { EventEmitter } from 'event-emitter';

import _ from 'lodash';

import Q from 'q';
import type { QPromise } from 'types/q';

import ko from 'rgs-knockout';
import type {
	Accessor,
	Observable,
	Computed,
} from 'rgs-knockout';


const stepsTemplate = require('../templates/steps.html');

import { Step, nullStep } from './step';
import type { StepId, StepOptions } from './step';

import StepsAnalyticsHelper from './helpers/analytics';
import type { AnalyticsOptions } from './helpers/analytics';


export type StepsOptions = {
	steps: Array<StepOptions>,
	scroll?: Accessor<boolean>,
	disableStepSummaryBodyClick: ?(Observable<boolean> | boolean),
	analytics?: AnalyticsOptions,
}

export class StepsBase extends EventEmitter {
	_options: StepsOptions;

	_steps: Array<Step>;

	_displayedSteps: Computed<Array<Step>>;
	_scroll: Computed<boolean>;
	_withAllClosedInProgress: Observable<boolean>;
	AllLocked: Computed<boolean>;

	constructor (options: Array<StepOptions> | StepsOptions) {
		super();

		if (_.isArray(options)) {
			options = ((options: any): Array<StepOptions>);
			options = ({
				steps: options,
			}: StepsOptions);
		}
		options = ((options: any): StepsOptions);

		this._options = options = _.defaults({}, options, this._defaultOptions());

		this._steps = this._options.steps.map((opts) => new Step(opts));

		this._displayedSteps = ko.pureComputed(() => this._steps.filter((step) => !step.Skip()));

		this._scroll = ko.pureComputed(() => ko.unwrap(this._options.scroll));

		this._disableStepSummaryBodyClick = this._options.disableStepSummaryBodyClick;

		this._withAllClosedInProgress = ko.observable(false);

		this._analyticsHelper = new StepsAnalyticsHelper(this._options.analytics);

		this.AllLocked = ko.pureComputed({
			read: () => _.every(this._steps, (step) => step.Locked()),
			write: (v) => {
				v = Boolean(v);
				_.each(this._steps, (step) => {
					step.Locked(v);
				});
			},
		});
	}

	_defaultOptions (): StepsOptions {
		return {
			steps: [],
			scroll: true,
			disableStepSummaryBodyClick: false,
			analytics: {
				productName: null,
			},
		};
	}

	_scrollOnTop (): boolean {
		return Boolean(this._scroll());
	}

	template () {
		return {
			html: stepsTemplate(),
			data: this,
		};
	}

	_stepSummaryClick (step: Step) {
		if (step.Locked()) {
			return;
		}
		this.open(step);
	}

	count (): number {
		return this._steps.length;
	}
	get (id: Step | StepId) {
		return _.find(this._steps, function (step) {
			return step.id === id || step === id;
		}) || nullStep;
	}
	getAll () {
		return this._steps.slice();
	}
	each (fn: (Step) => void): void {
		_.each(this._steps, fn);
	}
	map<T> (fn: (Step) => T): Array<T> {
		return _.map(this._steps, fn);
	}

	/*eslint-disable no-unused-vars*/
	isOpen (id: Step | StepId) {
		throw new Error("Not implemented");
	}
	open (id: Step | StepId) {
		throw new Error("Not implemented");
	}
	close (id: Step | StepId) {
		throw new Error("Not implemented");
	}
	closeAll () {
		throw new Error("Not implemented");
	}
	/*eslint-enable no-unused-vars*/

	withAllClosed<T> (promise: Promise<T>): QPromise<T> {
		this._withAllClosedInProgress(true);
		const snapshot = this.map(step => ({
			step,
			isOpen: this.isOpen(step),
		}));

		this.each(step => {
			this.close(step);
		});

		return Q(promise).finally(() => {
			_.each(snapshot, ({ step, isOpen }) => {
				if (isOpen) {
					this.open(step);
				} else {
					this.close(step);
				}
			});
			this._withAllClosedInProgress(false);
		});
	}

	withAllLocked<T> (promise: Promise<T>): QPromise<T> {
		const snapshot = this.map(step => ({
			step,
			isLocked: step.Locked(),
		}));

		this.each(step => {
			step.Locked(true);
		});

		return Q(promise).finally(() => {
			_.each(snapshot, ({ step, isLocked }) => {
				step.Locked(isLocked);
			});
		});
	}

	withAllClosedAndLocked<T> (promise: Promise<T>): QPromise<T> {
		return this.withAllClosed(this.withAllLocked(promise));
	}
	withAllLockedAndClosed<T> (promise: Promise<T>): QPromise<T> {
		return this.withAllClosedAndLocked(promise);
	}

	toSplunkJS () {
		return _.fromPairs(_.map(this._displayedSteps(), step => {
			return [ step.id, {
				isOpen: this.isOpen(step),
			}];
		}));
	}
}
